import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Mail, Menu, X } from "lucide-react";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const navigationItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={
        "fixed top-0 w-full z-50 transition-all duration-500 bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-100"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-4 group">
            <div className="relative">
              <img
                src="/logo.svg"
                alt="Theophrastus Research Lab logo"
                className="w-14 h-14 transition-all duration-300 group-hover:scale-105"
              />
            </div>
            <div>
              <h1
                className={
                  "text-xl font-bold transition-colors duration-300 text-gray-900 group-hover:text-[#7FB740]"
                }
              >
                Theophrastus Research Lab
              </h1>
              <p className={"text-sm text-gray-500 hidden md:block"}>
                Research • Consulting • Strategy
              </p>
            </div>
          </Link>

          <nav
            className={
              "hidden lg:flex items-center space-x-1 rounded-full px-2 py-2 bg-gray-100/80 backdrop-blur-md"
            }
          >
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-white text-gray-900 shadow-lg transform scale-105"
                      : "text-gray-700 hover:text-[#7FB740] hover:bg-[#7fb7401a]"
                  }`
                }
              >
                {item.label}
                <span className="sr-only">Navigate to {item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="mailto:info@trlab.eu"
              className={
                "flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-300 bg-[#7FB740] text-white hover:bg-[#6ea436] shadow-lg"
              }
              style={{
                background:
                  "linear-gradient(90deg, rgb(147, 199, 84), rgb(127, 183, 64), rgb(92, 138, 36))",
              }}
            >
              <Mail size={18} />
              <span>Contact</span>
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={
              "lg:hidden p-3 rounded-xl transition-colors text-gray-700 hover:bg-gray-100"
            }
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            title={isMenuOpen ? "Close menu" : "Open menu"}
          >
            <span className="sr-only">
              {isMenuOpen ? "Close menu" : "Open menu"}
            </span>
            {isMenuOpen ? (
              <X size={24} aria-hidden="true" />
            ) : (
              <Menu size={24} aria-hidden="true" />
            )}
          </button>
        </div>

        {isMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 rounded-b-2xl shadow-2xl"
          >
            <div className="py-6 space-y-2">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `block w-full text-left px-6 py-4 font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-[#7fb7401a] text-[#7FB740] border-r-4 border-[#7FB740]"
                        : "text-gray-700 hover:bg-gray-50 hover:text-[#7FB740]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="px-6 pt-4">
                <a
                  href="mailto:info@trlab.eu"
                  className="flex items-center space-x-2 bg-[#7FB740] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#6ea436] transition-colors w-full justify-center"
                >
                  <Mail size={18} />
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
